import { Landmark } from 'lucide-react';
import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import type { ModuleDefinition } from '../../app/modules';
import { useFinanceAvailable } from './api';

/**
 * Фінанси робіт (М13): прибутковість (замовлення, послуга, клієнт, юрист, КМ, МП, місяць, квартал; касова база чи
 * база завершення; CSV), фінанси замовлення (оплати з відсотками за одержувачем, реєстр витрат, погодження
 * гарантованих витрат, план/факт ЧЗ і маржі), «правило 100 USD», прогноз надходжень і дебіторка, дані для оплати
 * праці й бонусів, обмін CSV з pravdopfinance, параметри фінансового директора з датою дії й довідник одержувачів.
 * Розділ бачать ролі з finance:read; працівник без нього — лише тоді, коли йому адресовано запити причин
 * «правила 100 USD» (без сум). Фінанси замовлення відкриваються з вкладки «Оплати» картки замовлення
 * юридичного контуру або вибором замовлення в заголовку модуля.
 */
const page = (load: () => Promise<{ default: ComponentType }>): Pick<RouteObject, 'lazy'> => ({
  lazy: async () => ({ Component: (await load()).default }),
});

const financeModule: ModuleDefinition = {
  key: 'finance',
  label: 'Фінанси робіт',
  icon: Landmark,
  basePath: '/finance',
  // Області немає навмисно: юрист без finance:read бачить свої запити причин «правила 100 USD» (без сум).
  // Фінансові розділи й дані — лише з finance:read (перевіряють useFinanceAvailable, сторінки й сервер).
  useAccess: useFinanceAvailable,
  // Одразу після юридичного контуру (30): фінанси — це фінанси замовлень; 40 уже має модуль «ШІ-помічники».
  order: 35,
  routes: [
    {
      ...page(() => import('./FinanceLayout')),
      children: [
        { index: true, lazy: async () => ({ Component: (await import('./FinanceLayout')).FinanceIndex }) },
        { path: 'profitability', ...page(() => import('./pages/ProfitabilityPage')) },
        { path: 'approvals', ...page(() => import('./pages/ApprovalsPage')) },
        { path: 'rule100', ...page(() => import('./pages/Rule100Page')) },
        { path: 'cashflow', ...page(() => import('./pages/CashflowPage')) },
        { path: 'payroll', ...page(() => import('./pages/PayrollPage')) },
        { path: 'exchange', ...page(() => import('./pages/ExchangePage')) },
        { path: 'settings', ...page(() => import('./pages/SettingsPage')) },
      ],
    },
    { path: 'matters/:matterId', ...page(() => import('./pages/MatterFinancePage')) },
  ],
};

export default financeModule;
